import { useEffect, useState } from "react";
import { BellRing, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";
import { vibrantTheme } from "../common";
import { enablePush, pushSupported } from "../../lib/push";

/* Flight delay push alerts — enable notifications + track flights by number/date. */
export default function FlightAlerts() {
  const [flights, setFlights] = useState([]);
  const [code, setCode] = useState("");
  const [number, setNumber] = useState("");
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [enabled, setEnabled] = useState(false);
  const [busy, setBusy] = useState(false);
  const theme = vibrantTheme("flight-alerts");

  const load = () => api.get("/push/flights").then((r) => setFlights(r.data.items)).catch(() => {});
  useEffect(() => { load(); }, []);

  const enable = async () => {
    setBusy(true);
    try {
      await enablePush();
      setEnabled(true);
      try {
        await api.post("/push/test");
        toast.success("Notifications enabled — test alert sent");
      } catch {
        toast.success("Notifications enabled on this device");
      }
    } catch (e) {
      toast.error(e.message || "Could not enable notifications");
    } finally {
      setBusy(false);
    }
  };

  const track = async (e) => {
    e.preventDefault();
    try {
      await api.post("/push/track-flight", { code, number, date });
      toast.success(`Tracking ${code.toUpperCase()}${number} on ${date}`);
      setCode(""); setNumber("");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not track flight");
    }
  };

  const remove = async (id) => { await api.delete(`/push/flights/${id}`); load(); };

  const inp = "border border-zinc-300 bg-white px-3 py-2 text-sm w-full";

  return (
    <section className="border border-zinc-300 bg-white p-5 border-l-4" style={{ borderLeftColor: theme.bg }}
      data-testid="flight-alerts-card">
      <div className="flex items-center gap-2 mb-2">
        <BellRing className="w-5 h-5" style={{ color: theme.bg }} />
        <h3 className="font-heading font-900 text-lg">Flight Delay Alerts</h3>
      </div>
      <p className="text-sm text-zinc-700 mb-3">Get a push notification when a tracked flight is delayed, boarding or cancelled — even with the site closed.</p>
      {!pushSupported() && (
        <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 p-2 mb-3" data-testid="push-unsupported-note">
          Push isn't configured yet on this deployment (missing VAPID key) or your browser doesn't support it.
        </p>
      )}
      {!enabled && (
        <button onClick={enable} disabled={busy || !pushSupported()} data-testid="enable-push-btn"
          className="px-4 py-2 text-sm font-700 text-white disabled:opacity-50 mb-4" style={{ backgroundColor: theme.bg }}>
          {busy ? "Enabling..." : "Enable notifications"}
        </button>
      )}
      <form onSubmit={track} className="grid grid-cols-[70px_1fr_1fr_auto] gap-2 mb-3">
        <input value={code} onChange={(e) => setCode(e.target.value)} placeholder="DL" maxLength={3} required className={inp} data-testid="alert-code-input" />
        <input value={number} onChange={(e) => setNumber(e.target.value)} placeholder="Flight # e.g. 89" required className={inp} data-testid="alert-number-input" />
        <input type="date" value={date} onChange={(e) => setDate(e.target.value)} required className={inp} data-testid="alert-date-input" />
        <button type="submit" className="px-3 py-2 text-sm font-700 border" style={{ borderColor: theme.bg, color: theme.bg }} data-testid="track-flight-btn">Track</button>
      </form>
      {flights.length > 0 && (
        <ul className="space-y-1.5" data-testid="tracked-flights-list">
          {flights.map((f) => (
            <li key={f.id} className="flex items-center justify-between text-sm border border-zinc-200 px-3 py-1.5">
              <span className="font-700">{f.code}{f.number} <span className="font-400 text-muted-foreground">{f.date}</span>
                {f.last_status && <span className="ml-2 text-xs" style={{ color: theme.bg }}>{f.last_status}</span>}</span>
              <button onClick={() => remove(f.id)} aria-label="Stop tracking" data-testid={`untrack-${f.code}${f.number}`}>
                <Trash2 className="w-4 h-4 text-zinc-400 hover:text-red-500" />
              </button>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
